import type { ReactElement } from 'react';
import React, { useState } from 'react';
import {
  Button,
  Form,
  FormField,
  FormRow,
  FormSection,
  FormTitle,
  Modal,
  ModalBody,
  ModalFooter,
  Text,
} from '../ui';
import { useForm } from 'react-hook-form';
import type { ModalWithResultProps } from '../hooks/useModalWithResult';
import { stringToNumber } from '../../lib/zod-util';
import type { ScenarioOrder } from '../../lib/planningData';
import type {
  DuplicateOrdersModalQueryResult,
  DuplicateOrdersModalQueryVariables,
} from '../../graphql';
import {
  DuplicateOrdersModalDocument,
  useVcAndOrdersMutation,
} from '../../graphql';
import type { ApolloClient } from '@apollo/client';
import { useApolloClient } from '@apollo/client';
import { useNotify } from '../notifications/ToastNotifications';
import type { User } from '../../state/zustand';
import { useCurrentUser } from '../../state/zustand';
import { compact } from 'lodash';
import { isDefined, mapKeyBy } from '../../lib/util';
import type { VoyageCharterSchemaInput } from './schema';
import { voyageCharterSchema } from './schema';
import { fetchVoyageCharterData, saveVcAndOrders } from './voyageCharter';
import type { OrderWithVoyageCharterValues } from './orders';
import { voyageCharterOrderSchema } from './orders';
import {
  getVoyageCharterFormPersistedValues,
  removeLeftoverPortData,
} from './VoyageCharterForm';
import { stringToNumOpt } from '../core/util';
import { mapGroupBy } from '@seaber/common/lib/collections';

type DuplicateOrdersModalFields = {
  offsetByDays: number;
  occurrences: number;
};

export interface DuplicateOrdersModalParams {
  orders: ScenarioOrder[];
}

export interface DuplicateOrdersModalResult {
  offsetByDays: number;
  occurrences: number;
}

export type DuplicateOrdersModalProps = ModalWithResultProps<
  DuplicateOrdersModalResult,
  DuplicateOrdersModalParams
>;

/** Offset a date string by given number of days, return date string */
export function offsetDate(date: string, offsetByDays: number): string;
export function offsetDate(
  date: string | null | undefined,
  offsetByDays: number,
): string | null | undefined;
export function offsetDate(
  date: string | null | undefined,
  offsetByDays: number,
): string | null | undefined {
  if (!date) {
    return date;
  }
  const dateObj = new Date(date);
  dateObj.setDate(dateObj.getDate() + offsetByDays);
  return dateObj.toISOString();
}

const DuplicateOrdersModal = (
  props: DuplicateOrdersModalProps,
): ReactElement => {
  const notify = useNotify({ toastId: 'DUPLICATE_ORDERS_MODAL', type: 'info' });

  const client = useApolloClient();
  const currentUser = useCurrentUser();

  const [vcAndOrdersMutation] = useVcAndOrdersMutation({
    refetchQueries: ['VoyageCharter'],
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<DuplicateOrdersModalFields>({
    defaultValues: {
      offsetByDays: 0,
      occurrences: 1,
    },
  });

  const onSubmit = async (data: DuplicateOrdersModalFields) => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    const { offsetByDays, occurrences } = data;

    notify('Duplicating orders and voyage charters...', {
      type: 'info',
      autoClose: false,
    });

    try {
      const result = await duplicateOrdersWithOffset(
        client,
        vcAndOrdersMutation,
        currentUser,
        props.orders.map((order) => order.id),
        offsetByDays,
        occurrences,
      );

      if (result.success && (result.ordersCreated || 0) > 0) {
        notify(
          `${result.ordersCreated} orders in ${result.vcsCreated} voyage charters created successfully.`,
          { type: 'success' },
        );
        props.onComplete({ offsetByDays, occurrences });
      } else {
        notify(
          'No orders were successfully duplicated. Please check the logs for details.',
          {
            type: 'error',
            autoClose: false,
          },
        );
        setIsSubmitting(false);
      }
    } catch (err) {
      console.error('Error duplicating orders:', err);
      notify(`Failed to duplicate orders: ${err.message || 'Unknown error'}`, {
        type: 'error',
        autoClose: false,
      });
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={true} onClose={props.onCancel} width="400px" height="240px">
      <ModalBody p={0} overflowX="visible" overflowY="visible">
        <Form
          style={{ overflowY: 'visible', borderBottom: 'none' }}
          onSubmit={handleSubmit(onSubmit)}
        >
          <FormSection
            borderBottomStyle="none"
            marginTop={2}
            marginLeft={-2}
            marginBottom={-3}
          >
            <FormTitle>
              <Text fontSize="1.4em" fontWeight="bold">
                Duplicate {props.orders.length} orders
              </Text>
            </FormTitle>
          </FormSection>
          <FormSection borderBottomStyle="none">
            <FormRow>
              <FormField
                label="Offset by days"
                hasError={!!errors.offsetByDays}
                width={320}
              >
                <input
                  type="number"
                  {...register('offsetByDays', {
                    required: 'Offset by days is required',
                    setValueAs: stringToNumber,
                    validate: {
                      isInteger: (value) =>
                        Number.isInteger(value) || 'Must be an integer value',
                    },
                    min: { value: 0, message: 'Offset must be 0 or greater' },
                  })}
                  data-testid={`offsetByDays`}
                />
              </FormField>
            </FormRow>

            <FormRow>
              <FormField
                label="Repeat for occurrences"
                hasError={!!errors.occurrences}
                width={320}
              >
                <input
                  type="number"
                  {...register('occurrences', {
                    required: 'Number of occurrences is require',
                    setValueAs: stringToNumber,
                    validate: {
                      isInteger: (value) =>
                        Number.isInteger(value) || 'Must be an integer value',
                    },
                    min: {
                      value: 1,
                      message: 'Must have at least 1 occurrence',
                    },
                    max: {
                      value: 50,
                      message: 'Maximum 50 occurrences allowed',
                    },
                  })}
                  data-testid={`occurrences`}
                />
              </FormField>
            </FormRow>
          </FormSection>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button variant="secondary" mr="auto" onPress={props.onCancel}>
          Cancel
        </Button>
        <Button
          data-testid="duplicate-orders-modal-confirm-button"
          variant="positive"
          disabled={Object.keys(errors).length > 0 || isSubmitting}
          onPress={handleSubmit(onSubmit)}
        >
          Confirm
        </Button>
      </ModalFooter>
    </Modal>
  );
};

/** Duplicates orders and voyage charters with an offset in days. Gets the voyage charters for the listed orders, then duplicates every order related to those voyage charters even if the orders were not listed as params.
 * Function created by reusing and adapting the duplication logic from VoyageCharterModal/Form. Any changes to one, make sure to check if the other one also needs updates. */
async function duplicateOrdersWithOffset(
  client: ApolloClient<unknown>,
  vcAndOrdersMutation: ReturnType<typeof useVcAndOrdersMutation>[0],
  currentUser: User | null,
  orderIds: number[],
  offsetByDays: number,
  occurrences: number = 1,
): Promise<{ success: boolean; vcsCreated?: number; ordersCreated?: number }> {
  if (!isDefined(currentUser)) {
    throw new Error('Organization id missing');
  }
  const organizationId = currentUser.organizationId;

  if (!orderIds || orderIds.length === 0) {
    throw new Error('No order IDs provided for duplication');
  }

  // Validate order IDs
  for (const orderId of orderIds) {
    if (isNaN(orderId) || !Number.isFinite(orderId)) {
      throw new Error(`Invalid order ID: ${orderId} is not a valid number`);
    }
  }

  try {
    // Defined batch size for processing large number of orders to not choke db
    const BATCH_SIZE = 50;
    let totalVcsCreated = 0;
    let totalOrdersCreated = 0;

    const allCreateData: Parameters<typeof saveVcAndOrders>[0] = [];

    // Fetch original data only once
    const ordersByBatch: number[][] = [];
    for (let i = 0; i < orderIds.length; i += BATCH_SIZE) {
      ordersByBatch.push(orderIds.slice(i, i + BATCH_SIZE));
    }

    // Process each batch of orders and fetch data only once
    for (const batchOrderIds of ordersByBatch) {
      const { data } = await client.query<
        DuplicateOrdersModalQueryResult['data'],
        DuplicateOrdersModalQueryVariables
      >({
        query: DuplicateOrdersModalDocument,
        variables: { orderIds: batchOrderIds },
        fetchPolicy: 'network-only', // Fetch from server the data for duplication
      });

      if (!data) {
        throw new Error('Failed to query orders to duplicate');
      }

      const allOrders = compact(data.allOrders?.nodes);

      const ordersById = mapKeyBy(allOrders, (order) => order.id);
      for (const orderId of batchOrderIds) {
        const order = ordersById.get(orderId);
        if (!order) {
          throw new Error(
            `Order with ID ${orderId} could not be fetched for duplication`,
          );
        }
      }

      const ordersByIncomingVoyageCharterId = mapGroupBy(
        allOrders,
        (o) =>
          o.orderVoyageCharterRelationsByOrderId.nodes.find(
            (r) => r && !r.outgoing,
          )?.voyageCharterByVoyageCharterId?.id,
      );

      const voyageCharterDatasByVcId = mapKeyBy(
        await fetchVoyageCharterData(
          client,
          compact([...ordersByIncomingVoyageCharterId.keys()]),
        ),
        (vc) => vc.voyageCharter?.id,
      );
      for (let occurrence = 0; occurrence < occurrences; occurrence++) {
        const currentOffsetByDays = offsetByDays * (occurrence + 1);
        for (const [
          voyageCharterId,
          originalVcOrders,
        ] of ordersByIncomingVoyageCharterId.entries()) {
          if (!isDefined(voyageCharterId)) {
            // TODO: follow up ticket will implement duplicating orders without voyage charter
            console.warn(
              `Orders with IDs ${originalVcOrders.map(
                (o) => o.id,
              )} are not associated with a voyage charter`,
            );
            continue;
          }

          const vcData = voyageCharterDatasByVcId.get(voyageCharterId);
          if (!vcData) {
            throw new Error(
              `Voyage charter data expected for voyage charter ID ${voyageCharterId}`,
            );
          }

          const { voyageCharter, orders: vcOrders } = vcData;

          const persistedValues = getVoyageCharterFormPersistedValues(
            voyageCharter ?? null,
            { duplicate: true },
          );

          if (!persistedValues) {
            throw new Error(
              'Failed to get persisted values for voyage charter',
            );
          }

          const { duplicatedOrderIdByOriginalId } = persistedValues;

          const {
            extraFreights,
            commissions,
            chartererOrgId,
            shipownerOrgId,
            vesselId,
            layday,
            cancelling,
            relatedOrders,
            extraCosts,
            portData,
            ...baseContract
          } = persistedValues.voyageCharter;

          const voyageCharterData: VoyageCharterSchemaInput = {
            id: null,
            contractDetails:
              persistedValues.contract.type === 'spot'
                ? {
                    type: 'inquiry',
                  }
                : persistedValues.contract.type === 'coa'
                  ? {
                      ...persistedValues.contract,
                      isPreliminary: true,
                    }
                  : persistedValues.contract,
            baseContract: {
              ...baseContract,
              chartererOrgId: chartererOrgId?.value ?? null,
              shipownerOrgId: shipownerOrgId?.value ?? null,
              vesselId: vesselId?.value ?? null,
              layday:
                offsetDate(layday?.toISOString(), currentOffsetByDays) ?? null,
              cancelling:
                offsetDate(cancelling?.toISOString(), currentOffsetByDays) ??
                null,
              externalId: null,
            },
            relatedOrders: relatedOrders.map(({ order, ...relatedOrder }) => ({
              ...relatedOrder,
              freightAmount: !baseContract.lumpsum
                ? relatedOrder.freightAmount
                : null,
              orderId: order.id,
              coaRouteId: relatedOrder.coaRouteId?.value ?? null,
            })),
            commissions: commissions.map((commission) => ({
              ...commission,
              organizationId: commission.organizationId?.value ?? null,
            })),
            extraFreights: persistedValues?.voyageCharter.extraFreights,
            extraCosts,
            portData: removeLeftoverPortData(portData, relatedOrders),
          };

          const newVoyageCharter = voyageCharterSchema.parse(voyageCharterData);

          for (const vcOrder of vcOrders) {
            if (isNaN(vcOrder.id) || !Number.isFinite(vcOrder.id)) {
              console.warn(`Skipping invalid order with ID: ${vcOrder.id}`);
              continue;
            }
          }

          const validVcOrders = vcOrders.filter(
            (order) => !isNaN(order.id) && Number.isFinite(order.id),
          );

          const newOrders = validVcOrders.map((vcOrder) => {
            const duplicatedOrderTmpId = duplicatedOrderIdByOriginalId.get(
              vcOrder.id,
            );
            if (!isDefined(duplicatedOrderTmpId)) {
              throw new Error(
                `Original order ID not found for order ID ${vcOrder.id}`,
              );
            }

            const relatedOrder = relatedOrders.find(
              (o) => o.order.id === duplicatedOrderTmpId,
            );
            if (!relatedOrder) {
              throw new Error(
                `Related order not found for order ID ${vcOrder.id}`,
              );
            }
            return voyageCharterOrderSchema.parse({
              ...relatedOrder.order,
              loadPortId: vcOrder.loadPortId ?? NaN,
              dischargePortId: vcOrder.dischargePortId ?? NaN,
              cargoUnitId: stringToNumOpt(vcOrder.cargoUnitId) ?? null,
              orderName: relatedOrder.order.orderName || null,
            });
          });

          const newOrdersWithVcValues: OrderWithVoyageCharterValues[] =
            newOrders.map((order, i) => {
              const originalOrder = validVcOrders[i];
              return {
                ...order,
                coaId:
                  newVoyageCharter.contractDetails.type === 'coa'
                    ? newVoyageCharter.contractDetails.coaId
                    : null,
                chartererOrganizationId:
                  newVoyageCharter.baseContract.chartererOrgId,
                loadingRate: order?.loadingRate ?? null,
                loadingUnit: order?.loadingUnit ?? null,
                dischargingRate: order?.dischargingRate ?? null,
                dischargingUnit: order?.dischargingUnit ?? null,
                extraInfo: null,
                externalOrderId: null,
                layday: offsetDate(originalOrder.layday, currentOffsetByDays),
                cancelling: offsetDate(
                  originalOrder.cancelling,
                  currentOffsetByDays,
                ),
                rta: offsetDate(originalOrder.rta, currentOffsetByDays),
                deliveredBefore: offsetDate(
                  originalOrder.deliveredBefore,
                  currentOffsetByDays,
                ),
              };
            });

          allCreateData.push({
            newOrders: newOrdersWithVcValues,
            currentOrders: [],
            newVoyageCharter,
            currentVoyageCharter: null,
          });

          totalVcsCreated++;
          totalOrdersCreated += newOrders.length;
        }
      }
    }

    // Perform a single save operation with all the collected data
    if (allCreateData.length > 0) {
      const { voyageCharterIds: _newVoyageCharterIds } = await saveVcAndOrders(
        allCreateData,
        {},
        organizationId,
        currentUser,
        vcAndOrdersMutation,
        undefined,
      );
    }

    return {
      success: true,
      vcsCreated: totalVcsCreated,
      ordersCreated: totalOrdersCreated,
    };
  } catch (error) {
    console.error('Error in duplicateOrdersWithOffset:', error);
    throw error;
  }
}

export default DuplicateOrdersModal;
